/**
 * Icons that repeat across a page (review stars, product-card actions, mega-menu chevrons,
 * slider arrows, availability marks, the account glyph) are defined once here as <symbol>s and
 * referenced elsewhere via <Icon name="..." /> + <use>, instead of inlining the same SVG markup
 * per occurrence. Render this once, high in the tree (see app/layouts/Default.tsx).
 *
 * Symbol ids are the contract with Icon.tsx: `icon-${name}`, `name` typed in IconSprite/Icon.tsx.
 */
const IconSprite = () => (
  <svg aria-hidden="true" focusable="false" style={{ position: 'absolute', width: 0, height: 0, overflow: 'hidden' }}>
    <defs>
      {/* @solar-icons/react Star, weight="Bold" — used in ReviewCard */}
      <symbol id="icon-star" viewBox="0 0 24 24">
        <path
          fill="currentColor"
          d="M9.15316 5.40838C10.4198 3.13613 11.0531 2 12 2C12.9469 2 13.5802 3.13612 14.8468 5.40837L15.1745 5.99623C15.5345 6.64193 15.7144 6.96479 15.9951 7.17781C16.2757 7.39083 16.6251 7.4699 17.3241 7.62805L17.9605 7.77203C20.4201 8.32856 21.65 8.60682 21.9426 9.54773C22.2352 10.4886 21.3968 11.4691 19.7199 13.4299L19.2861 13.9372C18.8096 14.4944 18.5713 14.773 18.4641 15.1177C18.357 15.4624 18.393 15.8341 18.465 16.5776L18.5306 17.2544C18.7841 19.8706 18.9109 21.1787 18.1449 21.7602C17.3788 22.3417 16.2273 21.8115 13.9243 20.7512L13.3285 20.4768C12.6741 20.1755 12.3469 20.0248 12 20.0248C11.6531 20.0248 11.3259 20.1755 10.6715 20.4768L10.0757 20.7512C7.77268 21.8115 6.62118 22.3417 5.85515 21.7602C5.08912 21.1787 5.21588 19.8706 5.4694 17.2544L5.53498 16.5776C5.60703 15.8341 5.64305 15.4624 5.53586 15.1177C5.42868 14.773 5.19043 14.4944 4.71392 13.9372L4.2801 13.4299C2.60325 11.4691 1.76482 10.4886 2.05742 9.54773C2.35002 8.60682 3.57986 8.32856 6.03954 7.77203L6.67589 7.62805C7.37485 7.4699 7.72433 7.39083 8.00494 7.17781C8.28555 6.96479 8.46553 6.64194 8.82547 5.99623L9.15316 5.40838Z"
        />
      </symbol>

      {/* @solar-icons/react Eye, weight="Outline" — used in ProductCard (detail button) */}
      <symbol id="icon-eye" viewBox="0 0 24 24">
        <path
          fill="currentColor"
          fillRule="evenodd"
          clipRule="evenodd"
          d="M12 8.25C9.92893 8.25 8.25 9.92893 8.25 12C8.25 14.0711 9.92893 15.75 12 15.75C14.0711 15.75 15.75 14.0711 15.75 12C15.75 9.92893 14.0711 8.25 12 8.25ZM9.75 12C9.75 10.7574 10.7574 9.75 12 9.75C13.2426 9.75 14.25 10.7574 14.25 12C14.25 13.2426 13.2426 14.25 12 14.25C10.7574 14.25 9.75 13.2426 9.75 12Z"
        />
        <path
          fill="currentColor"
          fillRule="evenodd"
          clipRule="evenodd"
          d="M12 3.25C7.48587 3.25 4.44529 5.9542 2.68057 8.24686L2.64874 8.2882C2.24964 8.80653 1.88206 9.28392 1.63269 9.8484C1.36564 10.4529 1.25 11.1117 1.25 12C1.25 12.8883 1.36564 13.5471 1.63269 14.1516C1.88206 14.7161 2.24964 15.1935 2.64875 15.7118L2.68057 15.7531C4.44529 18.0458 7.48587 20.75 12 20.75C16.5141 20.75 19.5547 18.0458 21.3194 15.7531L21.3512 15.7118C21.7504 15.1935 22.1179 14.7161 22.3673 14.1516C22.6344 13.5471 22.75 12.8883 22.75 12C22.75 11.1117 22.6344 10.4529 22.3673 9.8484C22.1179 9.28391 21.7504 8.80652 21.3512 8.28818L21.3194 8.24686C19.5547 5.9542 16.5141 3.25 12 3.25ZM3.86922 9.1618C5.49864 7.04492 8.15036 4.75 12 4.75C15.8496 4.75 18.5014 7.04492 20.1308 9.1618C20.5694 9.73159 20.8263 10.0721 20.9952 10.4545C21.1532 10.812 21.25 11.2489 21.25 12C21.25 12.7511 21.1532 13.188 20.9952 13.5455C20.8263 13.9279 20.5694 14.2684 20.1308 14.8382C18.5014 16.9551 15.8496 19.25 12 19.25C8.15036 19.25 5.49864 16.9551 3.86922 14.8382C3.43064 14.2684 3.17374 13.9279 3.00476 13.5455C2.84684 13.188 2.75 12.7511 2.75 12C2.75 11.2489 2.84684 10.812 3.00476 10.4545C3.17374 10.0721 3.43063 9.73159 3.86922 9.1618Z"
        />
      </symbol>

      {/* @solar-icons/react Bag5, weight="Outline" — used in ProductCard (add-to-cart button) */}
      <symbol id="icon-bag5" viewBox="0 0 24 24">
        <path
          fill="currentColor"
          d="M15 11C15.5523 11 16 10.5523 16 10C16 9.44771 15.5523 9 15 9C14.4477 9 14 9.44771 14 10C14 10.5523 14.4477 11 15 11Z"
        />
        <path
          fill="currentColor"
          d="M10 10C10 10.5523 9.55228 11 9 11C8.44771 11 8 10.5523 8 10C8 9.44771 8.44771 9 9 9C9.55228 9 10 9.44771 10 10Z"
        />
        <path
          fill="currentColor"
          fillRule="evenodd"
          clipRule="evenodd"
          d="M8.25 5.27567V5C8.25 2.92893 9.92893 1.25 12 1.25C14.0711 1.25 15.75 2.92893 15.75 5V5.27567C16.4084 5.29605 16.9947 5.33261 17.5149 5.39818C18.7162 5.54961 19.6886 5.8682 20.4397 6.6018C20.6903 6.84656 20.9131 7.1183 21.1039 7.41205C21.676 8.29247 21.7977 9.30842 21.7106 10.516C21.6254 11.6979 21.3286 13.1818 20.952 15.0648L20.9328 15.1604C20.6715 16.4673 20.4635 17.5074 20.2061 18.3256C19.941 19.1683 19.6025 19.8497 19.0361 20.4017C18.8414 20.5915 18.6306 20.7643 18.4063 20.918C17.754 21.3651 17.0194 21.5634 16.1411 21.6581C15.2883 21.75 14.2276 21.75 12.8948 21.75H11.1053C9.77249 21.75 8.71181 21.75 7.85904 21.6581C6.98068 21.5634 6.24614 21.3651 5.59385 20.918C5.3695 20.7643 5.15877 20.5915 4.96399 20.4017C4.39766 19.8497 4.05914 19.1683 3.79405 18.3256C3.53667 17.5074 3.32867 16.4673 3.06729 15.1604L3.04822 15.065C2.67158 13.1819 2.37478 11.698 2.28954 10.516C2.20244 9.30842 2.32415 8.29247 2.89619 7.41205C3.08705 7.1183 3.30982 6.84656 3.56044 6.6018C4.31157 5.8682 5.28392 5.54961 6.48518 5.39818C7.00535 5.33261 7.59162 5.29605 8.25 5.27567ZM9.75 5C9.75 3.75736 10.7574 2.75 12 2.75C13.2426 2.75 14.25 3.75736 14.25 5V5.2522C13.8258 5.24999 13.378 5.25 12.9055 5.25H11.0946C10.6221 5.25 10.1743 5.24999 9.75 5.2522V5ZM4.60849 7.67491C5.02293 7.27015 5.61646 7.01956 6.67278 6.88641C7.74368 6.75141 9.1623 6.75 11.1486 6.75H12.8515C14.8378 6.75 16.2564 6.75141 17.3273 6.88641C18.3837 7.01956 18.9772 7.27015 19.3916 7.67491C19.5631 7.84238 19.7155 8.02831 19.8461 8.2293C20.1617 8.71507 20.2911 9.34622 20.2145 10.4081C20.1368 11.4847 19.86 12.8761 19.4705 14.8238C19.1987 16.1827 19.0055 17.1434 18.7752 17.8755C18.5498 18.5919 18.3103 19.0145 17.9892 19.3275C17.8559 19.4573 17.7118 19.5755 17.5582 19.6808C17.1884 19.9343 16.7271 20.0863 15.9803 20.1667C15.2173 20.249 14.2374 20.25 12.8515 20.25H11.1486C9.76271 20.25 8.78285 20.249 8.01978 20.1667C7.27307 20.0863 6.81176 19.9343 6.44188 19.6808C6.28838 19.5755 6.1442 19.4573 6.01093 19.3275C5.68979 19.0145 5.45028 18.5919 5.22492 17.8755C4.99463 17.1434 4.80147 16.1828 4.52967 14.8238C4.14013 12.8761 3.8633 11.4847 3.78565 10.4081C3.70906 9.34622 3.83838 8.71507 4.15401 8.2293C4.2846 8.02831 4.43702 7.84238 4.60849 7.67491Z"
        />
      </symbol>

      {/* @tabler/icons-react IconChevronDown — used in MegaMenu, LanguageSwitcher, CurrencySwitcher */}
      <symbol id="icon-chevron-down" viewBox="0 0 24 24">
        <path
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M6 9l6 6l6 -6"
        />
      </symbol>

      {/* @solar-icons/react AltArrowLeft, weight="Linear" — used in EmblaSliderArrowButtons */}
      <symbol id="icon-alt-arrow-left" viewBox="0 0 24 24">
        <path
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M15 5L9 12L15 19"
        />
      </symbol>

      {/* @solar-icons/react AltArrowRight, weight="Linear" — used in EmblaSliderArrowButtons */}
      <symbol id="icon-alt-arrow-right" viewBox="0 0 24 24">
        <path
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M9 5L15 12L9 19"
        />
      </symbol>

      {/* iconsax-reactjs Add, default (Linear) variant — used in LinksNav (footer accordion toggle) */}
      <symbol id="icon-add" viewBox="0 0 24 24">
        <path
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M6 12h12M12 18V6"
        />
      </symbol>

      {/* iconsax-reactjs Minus, default (Linear) variant — used in LinksNav (footer accordion toggle) */}
      <symbol id="icon-minus" viewBox="0 0 24 24">
        <path
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M6 12h12"
        />
      </symbol>

      {/* app/components/icons/CheckIcon.tsx — used via ProductAvailability (in-stock mark) */}
      <symbol id="icon-check" viewBox="0 0 11 9">
        <path
          fill="currentColor"
          d="M10.8526 1.30209L9.34829 0.0968962C9.30137 0.0499783 9.23539 0.0265188 9.15035 0.0265188C9.06532 0.0265188 8.99934 0.0499783 8.95242 0.0968962L4.54509 5.30476C4.49231 5.35754 4.42486 5.38393 4.34276 5.38393C4.26065 5.38393 4.19321 5.35754 4.14042 5.30476L1.84439 3.10549C1.79161 3.05271 1.72416 3.02632 1.64206 3.02632C1.55995 3.02632 1.49251 3.05271 1.43973 3.10549L0.137759 4.60099C0.0908417 4.65378 0.0673828 4.70509 0.0673828 4.75494C0.0673828 4.80479 0.0908417 4.85611 0.137759 4.90889L4.34276 8.91156C4.39554 8.95847 4.44686 8.98193 4.49671 8.98193C4.54656 8.98193 4.59494 8.95847 4.64186 8.91156L10.8526 1.69796C10.8995 1.65104 10.923 1.58507 10.923 1.50003C10.923 1.41499 10.8995 1.34901 10.8526 1.30209Z"
        />
      </symbol>

      {/* app/components/icons/XIcon.tsx — used via ProductAvailability (out-of-stock mark) */}
      <symbol id="icon-x" viewBox="0 0 460.775 460.775">
        <path
          fill="currentColor"
          d="M285.08,230.397L456.218,59.27c6.076-6.077,6.076-15.911,0-21.986L423.511,4.565c-2.913-2.911-6.866-4.55-10.992-4.55
	c-4.127,0-8.08,1.639-10.993,4.55l-171.138,171.14L59.25,4.565c-2.913-2.911-6.866-4.55-10.993-4.55
	c-4.126,0-8.08,1.639-10.992,4.55L4.558,37.284c-6.077,6.075-6.077,15.909,0,21.986l171.138,171.128L4.575,401.505
	c-6.074,6.077-6.074,15.911,0,21.986l32.709,32.719c2.911,2.911,6.865,4.55,10.992,4.55c4.127,0,8.08-1.639,10.994-4.55
	l171.117-171.12l171.118,171.12c2.913,2.911,6.866,4.55,10.993,4.55c4.128,0,8.081-1.639,10.992-4.55l32.709-32.719
	c6.074-6.075,6.074-15.909,0-21.986L285.08,230.397z"
        />
      </symbol>

      {/* app/components/account/CustomerIcon.tsx — used via UserNavigation (header + mega-menu footer) */}
      <symbol id="icon-customer" viewBox="0 0 24 24">
        <path
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M12 15C15.3137 15 18 12.3137 18 9C18 5.68629 15.3137 3 12 3C8.68629 3 6 5.68629 6 9C6 12.3137 8.68629 15 12 15Z"
        />
        <path
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M2.90625 20.2508C3.82775 18.6544 5.15328 17.3287 6.74958 16.407C8.34588 15.4853 10.1567 15 12 15C13.8433 15 15.6541 15.4853 17.2504 16.407C18.8467 17.3287 20.1722 18.6544 21.0938 20.2508"
        />
      </symbol>
    </defs>
  </svg>
);

export default IconSprite;
